import {SortOrder} from "../../shared/enums/sortOrder";

/**
 * Compare two nullable strings.
 * @param aVal The first string.
 * @param bVal The second string.
 * @param direction The sort direction.
 * @returns A negative value if aVal is less than bVal, a positive value if aVal is greater than bVal, or 0 if aVal and bVal are equal.
 */
export function compareStrings(
  aVal: string | undefined | null,
  bVal: string | undefined | null,
  direction: SortOrder
): number {
  return compareValues(aVal?.toUpperCase(), bVal?.toUpperCase(), direction);
}

/**
 * Compare two nullable numbers.
 * @param aVal The first number.
 * @param bVal The second number.
 * @param direction The sort direction.
 * @returns A negative value if aVal is less than bVal, a positive value if aVal is greater than bVal, or 0 if aVal and bVal are equal.
 */
export function compareNumbers(
  aVal: number | undefined | null,
  bVal: number | undefined | null,
  direction: SortOrder
): number {
  return compareValues(aVal, bVal, direction);
}

/**
 * Compare two nullable dates.
 * @param aVal The first date.
 * @param bVal The second date.
 * @param direction The sort direction.
 * @returns A negative value if aVal is less than bVal, a positive value if aVal is greater than bVal, or 0 if aVal and bVal are equal.
 */
export function compareDates(
  aVal: Date | undefined | null,
  bVal: Date | undefined | null,
  direction: SortOrder
): number {
  return compareValues(aVal?.getTime(), bVal?.getTime(), direction);
}

// This function is not exported, so that we don't need to consider comparing strings to numbers or dates.
function compareValues(
  aVal: number | string | undefined | null,
  bVal: number | string | undefined | null,
  direction: SortOrder
): number {
  const aIsNull = aVal === undefined || aVal === null;
  const bIsNull = bVal === undefined || bVal === null;

  if (aIsNull && bIsNull) return 0;

  // items sort equally
  if (aVal === bVal) {
    return 0;
  }

  // null sort after anything else
  if (aIsNull) {
    return 1;
  }
  if (bIsNull) {
    return -1;
  }

  if (direction === SortOrder.Ascending) return aVal < bVal ? -1 : 1;
  return aVal < bVal ? 1 : -1;
}
